<template>
  <div class="sessions-training-page">
    <section class="st-hero">
      <div class="st-hero__glow" />
      <div class="st-hero__top">
        <div class="d-flex align-items-center">
          <div class="st-hero__icon mr-1">
            <feather-icon
              icon="CalendarIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0">
              Training Sessions
            </h2>
            <p
              class="mb-0 st-hero__subtitle"
              :title="trainingName"
            >
              {{ trainingName }}
            </p>
          </div>
        </div>
        <div class="st-hero__actions">
          <button
            type="button"
            class="st-btn st-btn--back"
            @click="$router.push({ name: 'training' })"
          >
            <feather-icon
              icon="ArrowLeftIcon"
              size="16"
            />
            Back
          </button>
        </div>
      </div>

      <div
        v-if="!dataLoad && sessions.length"
        class="st-stats"
      >
        <div class="st-stat">
          <div class="st-stat__icon st-stat__icon--primary">
            <feather-icon
              icon="LayersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="st-stat__value">
              {{ sessions.length }}
            </div>
            <div class="st-stat__label">
              {{ sessions.length === 1 ? 'Session' : 'Sessions' }}
            </div>
          </div>
        </div>
        <div class="st-stat">
          <div class="st-stat__icon st-stat__icon--info">
            <feather-icon
              icon="UsersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="st-stat__value">
              {{ totalAttendees }}
            </div>
            <div class="st-stat__label">
              Attendees
            </div>
          </div>
        </div>
        <div class="st-stat">
          <div class="st-stat__icon st-stat__icon--success">
            <feather-icon
              icon="CheckCircleIcon"
              size="18"
            />
          </div>
          <div>
            <div class="st-stat__value">
              {{ totalAttended }}
            </div>
            <div class="st-stat__label">
              Attended
            </div>
          </div>
        </div>
        <div class="st-stat">
          <div class="st-stat__icon st-stat__icon--danger">
            <feather-icon
              icon="XCircleIcon"
              size="18"
            />
          </div>
          <div>
            <div class="st-stat__value">
              {{ totalAbsent }}
            </div>
            <div class="st-stat__label">
              Absent
            </div>
          </div>
        </div>
      </div>
    </section>

    <div
      v-if="dataLoad"
      class="st-loading"
    >
      <div class="st-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading session details…
      </p>
    </div>

    <div
      v-else-if="!sessions.length"
      class="st-empty"
    >
      <div class="st-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No session found
      </h5>
      <p class="mb-0 text-muted">
        Session details for this training will appear here once available.
      </p>
    </div>

    <section
      v-else
      class="st-workspace"
    >
      <div class="st-switcher">
        <button
          type="button"
          class="st-switcher__nav"
          :disabled="activeSessionIndex === 0"
          title="Previous session"
          @click="selectSession(activeSessionIndex - 1)"
        >
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
        </button>

        <div class="st-switcher__track">
          <button
            v-for="(session, sessionIndex) in sessions"
            :key="session.id || sessionIndex"
            type="button"
            class="st-tab"
            :class="{ 'is-active': sessionIndex === activeSessionIndex }"
            @click="selectSession(sessionIndex)"
          >
            <span class="st-tab__index">
              {{ sessionIndex + 1 }}
            </span>
            <span class="st-tab__body">
              <span class="st-tab__date">
                {{ formatDateRange(session.from, session.to) }}
              </span>
              <span class="st-tab__trainer">
                {{ session.trainer || 'No trainer' }}
              </span>
              <span class="st-tab__meta">
                {{ (session.attendees || []).length }} attendee{{ (session.attendees || []).length === 1 ? '' : 's' }}
              </span>
            </span>
          </button>
        </div>

        <button
          type="button"
          class="st-switcher__nav"
          :disabled="activeSessionIndex === sessions.length - 1"
          title="Next session"
          @click="selectSession(activeSessionIndex + 1)"
        >
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </button>
      </div>

      <transition
        name="st-fade"
        mode="out-in"
      >
        <div
          v-if="activeSession"
          :key="activeSession.id || activeSessionIndex"
          class="st-session-view"
        >
          <div class="st-session-view__header">
            <div class="st-session-view__title-row">
              <div class="st-session-view__icon">
                <feather-icon
                  icon="BookOpenIcon"
                  size="20"
                />
              </div>
              <div>
                <p class="st-session-view__kicker">
                  Session {{ activeSessionIndex + 1 }} of {{ sessions.length }}
                  <template v-if="activeSession.code">
                    · {{ activeSession.code }}
                  </template>
                </p>
                <h3 class="st-session-view__title">
                  {{ activeSession.training ? activeSession.training.name : trainingName }}
                </h3>
              </div>
            </div>
            <b-badge
              pill
              :variant="activeSession.is_internal ? 'light-info' : 'light-secondary'"
            >
              <feather-icon
                :icon="activeSession.is_internal ? 'HomeIcon' : 'GlobeIcon'"
                size="12"
                class="mr-25"
              />
              {{ activeSession.is_internal ? 'Internal' : 'External' }}
            </b-badge>
          </div>

          <div class="st-meta">
            <div class="st-meta__item">
              <feather-icon
                icon="CalendarIcon"
                size="16"
              />
              <div>
                <span class="st-meta__label">Date</span>
                <span class="st-meta__value">{{ formatDateRange(activeSession.from, activeSession.to) }}</span>
              </div>
            </div>
            <div class="st-meta__item">
              <feather-icon
                icon="ClockIcon"
                size="16"
              />
              <div>
                <span class="st-meta__label">Time</span>
                <span class="st-meta__value">{{ formatTimeRange(activeSession.start_time, activeSession.end_time) }}</span>
              </div>
            </div>
            <div class="st-meta__item">
              <feather-icon
                icon="MapPinIcon"
                size="16"
              />
              <div>
                <span class="st-meta__label">Venue</span>
                <span class="st-meta__value">{{ activeSession.training_venue || '—' }}</span>
              </div>
            </div>
            <div class="st-meta__item">
              <feather-icon
                icon="UserIcon"
                size="16"
              />
              <div>
                <span class="st-meta__label">Trainer</span>
                <span class="st-meta__value">{{ activeSession.trainer || '—' }}</span>
              </div>
            </div>
          </div>

          <div class="st-attendees">
            <header class="st-attendees__header">
              <div class="st-attendees__title">
                <feather-icon
                  icon="UsersIcon"
                  size="18"
                />
                Attendees
              </div>
              <div class="st-attendees__chips">
                <span class="st-chip st-chip--muted">
                  {{ activeAttendees.length }} total
                </span>
                <span class="st-chip st-chip--success">
                  {{ activeAttendedCount }} attended
                </span>
                <span class="st-chip st-chip--danger">
                  {{ activeAbsentCount }} absent
                </span>
              </div>
            </header>

            <div
              v-if="!activeAttendees.length"
              class="st-attendees__empty"
            >
              <feather-icon
                icon="UserIcon"
                size="24"
              />
              <p class="mb-0">
                No attendees registered for this session yet.
              </p>
            </div>

            <ul
              v-else
              class="st-attendees__list"
            >
              <li
                v-for="(attendee, attendeeIndex) in activeAttendees"
                :key="attendee.id || attendeeIndex"
                class="st-attendee"
              >
                <span class="st-attendee__index">
                  {{ attendeeIndex + 1 }}
                </span>
                <div class="st-attendee__avatar">
                  {{ attendeeInitials(attendee.attendee_name) }}
                </div>
                <div class="st-attendee__info">
                  <span class="st-attendee__name">
                    {{ attendee.attendee_name }}
                  </span>
                  <span
                    v-if="attendee.user && attendee.user.email"
                    class="st-attendee__email"
                  >
                    {{ attendee.user.email }}
                  </span>
                </div>
                <b-badge
                  pill
                  :variant="attendee.is_attended ? 'light-success' : 'light-danger'"
                  class="st-attendee__status"
                >
                  <feather-icon
                    :icon="attendee.is_attended ? 'CheckCircleIcon' : 'XCircleIcon'"
                    size="12"
                    class="mr-25"
                  />
                  {{ attendee.is_attended ? 'Attended' : 'Absent' }}
                </b-badge>
                <button
                  v-if="attendee.certificate_file"
                  type="button"
                  class="st-attendee__cert"
                  title="Download certificate"
                  :disabled="isFileLoading(activeSession, attendee, attendeeIndex)"
                  @click="getUrl(attendee.certificate_file.stream, activeSession, attendee, attendeeIndex)"
                >
                  <b-spinner
                    v-if="isFileLoading(activeSession, attendee, attendeeIndex)"
                    small
                    variant="light"
                  />
                  <template v-else>
                    <feather-icon
                      icon="DownloadIcon"
                      size="15"
                    />
                    Certificate
                  </template>
                </button>
                <span
                  v-else
                  class="st-attendee__no-cert"
                >
                  <feather-icon
                    icon="FileIcon"
                    size="14"
                  />
                  No certificate
                </span>
              </li>
            </ul>
          </div>
        </div>
      </transition>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'ShowUserTraining',
  data() {
    return {
      trainingSessionData: [],
      dataLoad: false,
      loadingFiles: {},
      activeSessionIndex: 0,
    }
  },
  computed: {
    trainingName() {
      return this.$route.params.name || 'Training program'
    },
    sessions() {
      return this.trainingSessionData || []
    },
    activeSession() {
      return this.sessions[this.activeSessionIndex] || null
    },
    activeAttendees() {
      return this.activeSession?.attendees || []
    },
    activeAttendedCount() {
      return this.activeAttendees.filter(attendee => attendee.is_attended).length
    },
    activeAbsentCount() {
      return this.activeAttendees.length - this.activeAttendedCount
    },
    totalAttendees() {
      return this.sessions.reduce((count, session) => count + (session.attendees || []).length, 0)
    },
    totalAttended() {
      return this.sessions.reduce((count, session) => (
        count + (session.attendees || []).filter(attendee => attendee.is_attended).length
      ), 0)
    },
    totalAbsent() {
      return this.totalAttendees - this.totalAttended
    },
  },
  mounted() {
    this.getTraining()
  },
  methods: {
    selectSession(index) {
      if (index < 0 || index >= this.sessions.length) return
      this.activeSessionIndex = index
    },
    attendeeInitials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      if (!parts.length) return '?'
      return parts.slice(0, 2).map(part => part[0].toUpperCase()).join('')
    },
    fileLoadingKey(session, attendee, attendeeIndex) {
      return `${session?.id || 'session'}-${attendee?.id || attendeeIndex}`
    },
    isFileLoading(session, attendee, attendeeIndex) {
      return !!this.loadingFiles[this.fileLoadingKey(session, attendee, attendeeIndex)]
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(`${value}T00:00:00`)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
    formatDateRange(from, to) {
      const start = this.formatDate(from)
      const end = this.formatDate(to)
      if (start && end && start !== end) return `${start} – ${end}`
      return start || end || '—'
    },
    formatTime(value) {
      if (!value) return ''
      return String(value).slice(0, 5)
    },
    formatTimeRange(startTime, endTime) {
      const from = this.formatTime(startTime)
      const to = this.formatTime(endTime)
      if (from && to) return `${from} – ${to}`
      return from || to || '—'
    },
    getTraining() {
      this.dataLoad = true
      axios.get(`training-session?training_id=${this.$route.params.id}`)
        .then(res => {
          this.trainingSessionData = decryptData(res.data.payload).data.trainingSessions || []
          this.activeSessionIndex = 0
        })
        .catch(() => {
          this.trainingSessionData = []
          this.activeSessionIndex = 0
        })
        .finally(() => {
          this.dataLoad = false
        })
    },
    sanitizeFilenamePart(value) {
      return String(value || '')
        .trim()
        .replace(/[\\/:*?"<>|]/g, '-')
        .replace(/\s+/g, ' ')
        .slice(0, 120)
    },
    async getUrl(linkUrl, session, attendee, attendeeIndex) {
      const loadingKey = this.fileLoadingKey(session, attendee, attendeeIndex)
      try {
        this.$set(this.loadingFiles, loadingKey, true)
        const response = await axios.get(linkUrl, {
          responseType: 'blob',
        })

        if (response.status === 200) {
          const blob = new Blob([response.data], { type: response.data.type })
          const url = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          const trainingNamePart = this.sanitizeFilenamePart(session?.training?.name)
          const attendeeName = this.sanitizeFilenamePart(attendee?.attendee_name)
          const filename = `${trainingNamePart} - ${attendeeName}`.trim() || 'certificate'
          link.download = `${filename}.pdf`

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `${error}`,
            icon: 'BellIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.$set(this.loadingFiles, loadingKey, false)
      }
    },
  },
}
</script>

<style lang="scss" src="./show-user-training.scss"></style>
